import React from 'react';
import { Control, Controller, ControllerRenderProps } from 'react-hook-form';
import DateInput from '../Date/DateInput';

/**
 * @param {string} name - The form variable name that is being referenced.
 * @param {string} label - The text caption that will be displayed.
 * @param {Control<any, any>} - Object that is passed to the controller to manage controlled components.
 * @param {moment.Moment} defaultValue - Initial value to ensure that each form component has a defined starting value. Optional
 * @param {string} className  - Custom styling. Optional
 * @param {boolean} disabled - Disables input to the component. Optional
 * @param {(date : moment.Moment) => void} onDateChange - Callback function that is called whenever the date changes. Optional
 * 
 */

interface IControlledDateInputProps {
    name : string;
    label : string;
    control : Control<any, any>;
    defaultValue ?: moment.Moment;
    className ?: string;
    disabled ?: boolean;
    onDateChange ?: (date : moment.Moment | null) => void;
}

/**
 * @requires react-hook-form - this component is only meant to be used with react-hook-form
 */
const ControlledDateInput = (props : IControlledDateInputProps) : React.ReactElement => {

    const onChange = (date : moment.Moment | null, field : ControllerRenderProps) : void => {
        field.onChange(date);
        if (props.onDateChange) {
            props.onDateChange(date);
        }
    };

    return (
        <Controller
            name={props.name}
            control={props.control}
            defaultValue={props.defaultValue || null}
            render={({ field, fieldState }) => (
                <DateInput
                    {...field}
                    label={props.label}
                    className={props.className}
                    error={fieldState.error?.message ?? ''}
                    disabled={props.disabled}
                    onDateChange={(date : moment.Moment | null) : void => onChange(date, field)}
                />
            )}
        />
    );
};

export default ControlledDateInput;
